/** 将 OpenClaw Hook 转换为推推 Agent 执行中间步骤；队列、HTTP 与错误隔离由 SDK 负责。 */
import type { OpenClawPluginApi } from 'openclaw/plugin-sdk/core';
import { DEFAULT_ACCOUNT_ID } from 'openclaw/plugin-sdk/account-id';
import type { TuituiAgentEventContext, TuituiBotClient } from '@qihoo/tuitui-bot-sdk';
import { CHANNEL_ID } from './const';
import { monitorEnabledDefault } from './accounts';
import { createTuituiClient } from './sdk';
import { getOpenClawConfig } from './utils';

const stringValue = (value: unknown): string => typeof value === 'string' ? value.trim() : '';

interface MonitorTarget { accountId: string; appId: string; appSecret: string; agentId: string }

// 相同账号和凭据复用同一个 Client，确保 Agent 事件通过同一 SDK 队列按序上报。
const clients = new Map<string, TuituiBotClient>();

function clientFor(target: MonitorTarget): TuituiBotClient {
  const key = `${target.accountId}\0${target.appId}\0${target.appSecret}`;
  let client = clients.get(key);
  if (!client) {
    client = createTuituiClient({ appId: target.appId, appSecret: target.appSecret });
    clients.set(key, client);
  }
  return client;
}

export function extractAgentId(sessionKey: string): string | null {
  const parts = sessionKey.split(':');
  return parts[0] === 'agent' && parts[1] ? parts[1] : null;
}

function targetFor(api: OpenClawPluginApi, accountId: unknown, agentId: string | null): MonitorTarget | null {
  const cfg = getOpenClawConfig(api.runtime);
  const channelCfg = cfg?.channels?.[CHANNEL_ID] || {};
  if (!agentId) return null;
  const specified = stringValue(accountId);
  const agentByAccount = new Map<string, string>(
    (cfg?.bindings ?? [])
      .filter((binding: any) => binding?.match?.channel === CHANNEL_ID && binding.match.accountId && binding.agentId)
      .map((binding: any) => [binding.match.accountId, binding.agentId] as const),
  );
  const accounts = [[DEFAULT_ACCOUNT_ID, channelCfg, undefined], ...Object.entries(channelCfg.accounts ?? {})];
  const matches = accounts.flatMap(([id, account, inherited]) => {
    if ((account?.monitorEnabled ?? monitorEnabledDefault) !== true) return [];
    const target = {
      accountId: id,
      appId: account?.appId || inherited?.appId || '',
      appSecret: account?.appSecret || inherited?.appSecret || '',
      agentId: agentByAccount.get(id) ?? 'main',
    };
    return target.appId && target.appSecret && target.agentId === agentId ? [target] : [];
  });
  if (specified) return matches.find((target) => target.accountId === specified) ?? null;
  return matches[0] ?? null;
}

function resolveReport(
  api: OpenClawPluginApi,
  context: any,
): { client: TuituiBotClient; context: TuituiAgentEventContext } | null {
  const sessionKey = stringValue(context.sessionKey);
  const target = targetFor(api, context.accountId, extractAgentId(sessionKey));
  if (!target || !sessionKey) {
    console.log(`[${CHANNEL_ID}][monitor] skipped: unknown account or session, sessionKey=${sessionKey}, accountId=${context.accountId}`);
    return null;
  }
  // message_received 没有 runId，后续事件才会提供；缺失时保留为空字符串。
  const runId = stringValue(context.runId);
  const messageId = stringValue(context.messageId);
  return {
    client: clientFor(target),
    context: { sessionKey, runId, messageId },
  };
}

function onMessage(api: OpenClawPluginApi, context: any): void {
  const report = resolveReport(api, context);
  if (!report) return;
  report.client.agent.report.message_received(report.context);
}

function onLLMInput(api: OpenClawPluginApi, event: any, context: any): void {
  const report = resolveReport(api, context);
  if (!report) return;
  const model = stringValue(event.resolvedRef) || stringValue(event.model);
  const prompt = stringValue(event.prompt);
  const payload = { model, prompt };
  report.client.agent.report.llm_input(report.context, payload);
}

function onLLMOutput(api: OpenClawPluginApi, event: any, context: any): void {
  const report = resolveReport(api, context);
  if (!report) return;
  const payload = {
    assistantTexts: event.assistantTexts,
    thinking: '',
    usage: event.usage,
  };
  report.client.agent.report.llm_output(report.context, payload);
}

function onBeforeTool(api: OpenClawPluginApi, event: any, context: any): void {
  const report = resolveReport(api, context);
  if (!report) return;
  const payload = {
    toolCallId: stringValue(event.toolCallId),
    toolName: stringValue(event.toolName),
    params: event.params,
  };
  report.client.agent.report.before_tool_call(report.context, payload);
}

function onAfterTool(api: OpenClawPluginApi, event: any, context: any): void {
  const report = resolveReport(api, context);
  if (!report) return;
  const payload = {
    toolCallId: stringValue(event.toolCallId),
    toolName: stringValue(event.toolName),
    result: event.result !== undefined ? event.result : (typeof event.error === 'string' ? event.error : null),
  };
  report.client.agent.report.after_tool_call(report.context, payload);
}

function onAgentEnd(api: OpenClawPluginApi, context: any): void {
  const report = resolveReport(api, context);
  if (!report) return;
  report.client.agent.report.agent_end(report.context);
}

/** 注册插件需要监控的 Agent 执行中间步骤 Hook。见 OpenClaw 代码的 PluginHookHandlerMap。 */
export function registerMonitorHooks(api: OpenClawPluginApi): void {
  api.on('message_received', (_event, context) => onMessage(api, context));
  api.on('llm_input', (event, context) => onLLMInput(api, event, context));
  api.on('llm_output', (event, context) => onLLMOutput(api, event, context));
  api.on('before_tool_call', (event, context) => onBeforeTool(api, event, context));
  api.on('after_tool_call', (event, context) => onAfterTool(api, event, context));
  api.on('agent_end', (_event, context) => onAgentEnd(api, context));
}
